import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { formatINR, GST_RATES } from "@/lib/constants";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Plus, FileText, Trash2, Eye, Search, CheckSquare, Package, Download, MoreVertical, AlertTriangle, Upload, Globe, RefreshCw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import HsnCombobox from "@/components/HsnCombobox";

export default function InvoicesPage() {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [catalogItems, setCatalogItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState("");
  const [items, setItems] = useState([{ description: "", hsn_sac: "", quantity: 1, rate: "", gst_rate: "18", discount_type: "", discount_value: "" }]);
  const [notes, setNotes] = useState("");
  const [creating, setCreating] = useState(false);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [confirmDelete, setConfirmDelete] = useState(false);
  const navigate = useNavigate();

  // Export invoice state
  const [placeOfSupply, setPlaceOfSupply] = useState("");
  const [exportType, setExportType] = useState(user?.export_type_preference || "lut");
  const [shippingBillNumber, setShippingBillNumber] = useState("");
  const [shippingBillDate, setShippingBillDate] = useState("");
  const [portCode, setPortCode] = useState("");
  const [states, setStates] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [exportCurrency, setExportCurrency] = useState(user?.default_export_currency || "USD");
  const [exchangeRate, setExchangeRate] = useState("");
  // FX-aware: live ECB rate metadata
  const [fxMeta, setFxMeta] = useState(null); // { rate, date, source, cached, stale }
  const [fxLoading, setFxLoading] = useState(false);
  const [fxManualOverride, setFxManualOverride] = useState(false);

  const isExportDest = placeOfSupply === "Outside India" || placeOfSupply === "SEZ";
  const isGoodsUser = ["Solar & Renewable Energy", "Gems & Jewellery", "General Trading", "Construction"].includes(user?.business_sector);

  // Auto-detect foreign clients: if the selected client's state is "Outside India" or "SEZ",
  // pre-set Place of Supply to drive the export-invoice path.
  useEffect(() => {
    if (!selectedClient) return;
    const c = clients.find((x) => x.client_id === selectedClient);
    if (!c) return;
    if ((c.state === "Outside India" || c.state === "SEZ") && !placeOfSupply) {
      setPlaceOfSupply(c.state);
    }
  }, [selectedClient, clients]);

  // FX-aware export invoices: when destination + currency are set, pull live ECB rate.
  // Re-fetch when currency changes. Skip if user has manually typed a rate (override preserved).
  const fetchFxRate = async (refresh = false) => {
    if (!isExportDest || !exportCurrency || exportCurrency === "INR") return;
    setFxLoading(true);
    try {
      const res = await api.get(`/fx/rate?from=${exportCurrency}&to=INR${refresh ? "&refresh=true" : ""}`);
      setFxMeta(res.data);
      if (!fxManualOverride || refresh) {
        setExchangeRate(String(res.data.rate.toFixed(4)));
        if (refresh) setFxManualOverride(false);
      }
    } catch (err) {
      // Silent fail — user can still type rate manually
      console.warn("FX fetch failed", err);
      setFxMeta(null);
    } finally {
      setFxLoading(false);
    }
  };

  useEffect(() => {
    if (isExportDest && exportCurrency && exportCurrency !== "INR") {
      fetchFxRate(false);
    } else {
      setFxMeta(null);
    }
  }, [isExportDest, exportCurrency]);

  const showGstCol = user?.gst_status !== "registered_composition" && user?.gst_status !== "unregistered";

  useEffect(() => {
    Promise.all([api.get("/invoices"), api.get("/clients"), api.get("/items"), api.get("/states"), api.get("/currencies")])
      .then(([invRes, cliRes, itemsRes, statesRes, currRes]) => {
        setInvoices(invRes.data);
        setClients(cliRes.data);
        setCatalogItems(itemsRes.data);
        setStates(statesRes.data);
        setCurrencies(currRes.data);
      })
      .finally(() => setLoading(false));
  }, []);

  const addItem = () => setItems([...items, { description: "", hsn_sac: "", quantity: 1, rate: "", gst_rate: "18", discount_type: "", discount_value: "" }]);
  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));
  const updateItem = (idx, field, value) => {
    const updated = [...items];
    updated[idx] = { ...updated[idx], [field]: value };
    setItems(updated);
  };

  const addFromCatalog = (catalogItem) => {
    setItems([
      ...items.filter((i) => i.description || i.rate),
      {
        description: catalogItem.name,
        hsn_sac: catalogItem.hsn_sac || "",
        quantity: 1,
        rate: String(catalogItem.rate || ""),
        gst_rate: String(catalogItem.gst_rate || "18"),
        discount_type: "",
        discount_value: "",
      },
    ]);
  };

  const createInvoice = async () => {
    if (!selectedClient) { toast.error("Select a client"); return; }
    if (items.some((i) => !i.description || !i.rate)) { toast.error("Fill in all item details"); return; }

    // Shipping bill validation for export goods
    if (isExportDest && isGoodsUser && shippingBillDate) {
      const today = new Date().toISOString().split("T")[0];
      if (shippingBillDate < today) {
        toast.warning("Shipping Bill Date is before today's date — this may be an audit red flag.");
      }
    }

    setCreating(true);
    try {
      const response = await api.post("/invoices", {
        client_id: selectedClient,
        items: items.map((i) => ({
          ...i, quantity: Number(i.quantity), rate: Number(i.rate), gst_rate: Number(i.gst_rate),
          discount_type: i.discount_type || "",
          discount_value: Number(i.discount_value) || 0,
        })),
        notes,
        place_of_supply: placeOfSupply,
        is_export: isExportDest,
        export_type: isExportDest ? exportType : "",
        shipping_bill_number: isExportDest ? shippingBillNumber : "",
        shipping_bill_date: isExportDest ? shippingBillDate : "",
        port_code: isExportDest ? portCode : "",
        currency: isExportDest ? exportCurrency : "",
        exchange_rate: isExportDest && exchangeRate ? Number(exchangeRate) : 0,
      });
      toast.success("Invoice created!");
      setDialogOpen(false);
      navigate(`/invoices/${response.data.invoice_id}/review`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create invoice");
    } finally {
      setCreating(false);
    }
  };

  const toggleSelect = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const toggleAll = () => {
    if (selected.size === filtered.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map((i) => i.invoice_id)));
    }
  };

  const executeBulk = async (status) => {
    if (selected.size === 0) return;
    try {
      await api.post("/invoices/bulk-status", { invoice_ids: [...selected], status });
      setInvoices(invoices.map((inv) => (selected.has(inv.invoice_id) ? { ...inv, status } : inv)));
      toast.success(`Updated ${selected.size} invoices to ${status}`);
      setSelected(new Set());
    } catch {
      toast.error("Bulk update failed");
    }
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    try {
      await api.post("/invoices/bulk-delete", { ids: [...selected] });
      setInvoices(invoices.filter((inv) => !selected.has(inv.invoice_id)));
      toast.success(`Deleted ${selected.size} invoices`);
      setSelected(new Set());
      setConfirmDelete(false);
    } catch {
      toast.error("Bulk delete failed");
    }
  };

  const bulkExportCSV = async () => {
    if (selected.size === 0) return;
    try {
      const response = await api.post("/invoices/bulk-export-csv", { ids: [...selected] }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "invoices_export.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV exported!");
    } catch {
      toast.error("Export failed");
    }
  };

  const deleteInvoice = async (id) => {
    try {
      await api.post("/invoices/bulk-delete", { ids: [id] });
      setInvoices(invoices.filter((inv) => inv.invoice_id !== id));
      toast.success("Invoice deleted");
    } catch {
      toast.error("Failed to delete");
    }
  };

  const filtered = invoices
    .filter((i) => filter === "all" || i.status === filter)
    .filter((i) => {
      if (!search) return true;
      const q = search.toLowerCase();
      return i.invoice_number?.toLowerCase().includes(q) || i.client_name?.toLowerCase().includes(q);
    });

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="invoices-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-outfit text-2xl font-bold text-[#0f172a] tracking-tight">Invoices</h1>
          <p className="text-sm text-[#64748b] mt-1">{invoices.length} total invoices</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate("/invoices/import")} data-testid="import-invoices-btn"
            className="border-[#e2e8f0] rounded-xl">
            <Upload className="h-4 w-4 mr-2" /> Import
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-invoice-btn" className="bg-[#4338ca] hover:bg-[#3730a3] rounded-xl shadow-sm">
              <Plus className="h-4 w-4 mr-2" /> New Invoice
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-outfit text-xl">Create Invoice</DialogTitle>
              <DialogDescription className="text-sm text-[#64748b]">Select a client and add line items</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label className="text-sm font-medium">Client *</Label>
                <Select value={selectedClient} onValueChange={setSelectedClient}>
                  <SelectTrigger data-testid="invoice-client-select" className="mt-1.5"><SelectValue placeholder="Select client" /></SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => <SelectItem key={c.client_id} value={c.client_id}>{c.name} ({c.state})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {/* Place of Supply / Export Destination */}
              {user?.gst_status === "registered_regular" && (
                <div className="space-y-3">
                  <div>
                    <Label className="text-sm font-medium">Place of Supply / Destination</Label>
                    <Select value={placeOfSupply} onValueChange={setPlaceOfSupply}>
                      <SelectTrigger data-testid="invoice-place-of-supply" className="mt-1.5">
                        <SelectValue placeholder="Auto from client state" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value=" ">Auto from client state</SelectItem>
                        <SelectItem value="Outside India">Outside India (Export)</SelectItem>
                        <SelectItem value="SEZ">Special Economic Zone (SEZ)</SelectItem>
                        {states.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {isExportDest && (
                      <p
                        className="text-[10px] text-[#4338ca] mt-1 flex items-center gap-1"
                        data-testid="foreign-client-banner"
                      >
                        <Globe className="h-3 w-3" />
                        Foreign client detected — switched to export invoice mode with live FX.
                      </p>
                    )}
                  </div>

                  {/* Export toggle: LUT vs IGST */}
                  {isExportDest && (
                    <div className="bg-[#4338ca]/[0.04] rounded-xl border border-[#4338ca]/20 p-4 space-y-3">
                      <div className="flex items-center gap-2">
                        <Globe className="h-4 w-4 text-[#4338ca]" />
                        <span className="text-sm font-semibold text-[#0f172a]">Export Invoice Type</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <button type="button" onClick={() => setExportType("lut")}
                          data-testid="export-type-lut"
                          className={`p-3 rounded-lg border text-left transition-all ${exportType === "lut" ? "border-[#4338ca] bg-[#4338ca]/10 ring-1 ring-[#4338ca]" : "border-[#e2e8f0] hover:border-[#94a3b8]"}`}>
                          <div className="text-sm font-semibold text-[#0f172a]">Supply under LUT</div>
                          <div className="text-xs text-[#64748b] mt-0.5">Zero Tax — No IGST charged</div>
                          {user?.lut_number && <div className="text-[10px] text-[#4338ca] font-mono mt-1">LUT: {user.lut_number}</div>}
                        </button>
                        <button type="button" onClick={() => setExportType("igst")}
                          data-testid="export-type-igst"
                          className={`p-3 rounded-lg border text-left transition-all ${exportType === "igst" ? "border-[#4338ca] bg-[#4338ca]/10 ring-1 ring-[#4338ca]" : "border-[#e2e8f0] hover:border-[#94a3b8]"}`}>
                          <div className="text-sm font-semibold text-[#0f172a]">With Payment of IGST</div>
                          <div className="text-xs text-[#64748b] mt-0.5">IGST charged — Refundable</div>
                        </button>
                      </div>

                      {/* Multi-Currency */}
                      <div className="mt-2 space-y-2">
                        <div className="text-xs font-medium text-[#64748b] uppercase tracking-wider">Currency & Exchange Rate</div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-xs text-[#64748b]">Foreign Currency</Label>
                            <Select value={exportCurrency} onValueChange={setExportCurrency}>
                              <SelectTrigger data-testid="export-currency-select" className="text-sm mt-1">
                                <SelectValue>{exportCurrency || "Select"}</SelectValue>
                              </SelectTrigger>
                              <SelectContent>
                                {currencies.map((c) => (
                                  <SelectItem key={c.code} value={c.code}>{c.code} — {c.symbol} {c.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs text-[#64748b] flex items-center justify-between">
                              <span>Exchange Rate (1 {exportCurrency} = ? INR)</span>
                              {fxMeta && (
                                <button
                                  type="button"
                                  onClick={() => fetchFxRate(true)}
                                  disabled={fxLoading}
                                  data-testid="fx-refresh-btn"
                                  className="flex items-center gap-1 text-[10px] text-[#4338ca] hover:text-[#3730a3] disabled:opacity-50"
                                  title="Re-fetch live ECB rate"
                                >
                                  {fxLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                                  Refresh
                                </button>
                              )}
                            </Label>
                            <Input
                              type="number"
                              value={exchangeRate}
                              onChange={(e) => { setExchangeRate(e.target.value); setFxManualOverride(true); }}
                              placeholder={fxLoading ? "Fetching live rate…" : "e.g., 84.50"}
                              data-testid="exchange-rate-input"
                              className="text-sm mt-1 font-mono"
                            />
                            {fxMeta && (
                              <p
                                className="text-[10px] text-[#10b981] mt-1 flex items-center gap-1"
                                data-testid="fx-source-caption"
                              >
                                <Globe className="h-3 w-3" />
                                Live ECB rate · {fxMeta.date}{fxMeta.cached ? " · cached" : ""}{fxMeta.stale ? " · stale" : ""}
                                {fxManualOverride && <span className="text-[#f59e0b] ml-1">· manually edited</span>}
                              </p>
                            )}
                            {!fxMeta && fxLoading && (
                              <p className="text-[10px] text-[#94a3b8] mt-1">Fetching today&apos;s rate…</p>
                            )}
                            {exchangeRate > 0 && (
                              <p className="text-[10px] text-[#94a3b8] mt-0.5">Invoice will show amounts in both INR and {exportCurrency}</p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Shipping & Logistics for Goods sectors */}
                      {isGoodsUser && (
                        <div className="mt-2 space-y-2">
                          <div className="text-xs font-medium text-[#64748b] uppercase tracking-wider">Shipping & Logistics</div>
                          <div className="grid grid-cols-3 gap-2">
                            <div>
                              <Label className="text-xs text-[#64748b]">Shipping Bill No.</Label>
                              <Input value={shippingBillNumber} onChange={(e) => setShippingBillNumber(e.target.value)}
                                placeholder="e.g., 9876543" data-testid="shipping-bill-number" className="text-sm mt-1 font-mono" />
                            </div>
                            <div>
                              <Label className="text-xs text-[#64748b]">Shipping Bill Date</Label>
                              <Input type="date" value={shippingBillDate} onChange={(e) => setShippingBillDate(e.target.value)}
                                data-testid="shipping-bill-date" className="text-sm mt-1" />
                            </div>
                            <div>
                              <Label className="text-xs text-[#64748b]">Port Code</Label>
                              <Input value={portCode} onChange={(e) => setPortCode(e.target.value)}
                                placeholder="e.g., INMUN1" data-testid="port-code" className="text-sm mt-1 font-mono" />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {catalogItems.length > 0 && (
                <div>
                  <Label className="text-sm font-medium mb-2 block">Add from Catalog</Label>
                  <div className="flex flex-wrap gap-1.5">
                    {catalogItems.slice(0, 8).map((ci) => (
                      <button
                        key={ci.item_id}
                        onClick={() => addFromCatalog(ci)}
                        data-testid={`catalog-item-${ci.item_id}`}
                        className="text-xs px-2.5 py-1 rounded-full bg-[#4338ca]/5 text-[#4338ca] hover:bg-[#4338ca]/15 transition-colors flex items-center gap-1"
                      >
                        <Package className="h-3 w-3" /> {ci.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <Label className="text-sm font-medium mb-2 block">Line Items</Label>
                {items.map((item, idx) => (
                  <div key={idx} className="mb-4 pb-3 border-b border-[#f1f5f9] last:border-0">
                    <div className="grid grid-cols-12 gap-2 items-end">
                      <div className={showGstCol ? "col-span-3" : "col-span-4"}>
                        <Input placeholder="Description" value={item.description} onChange={(e) => updateItem(idx, "description", e.target.value)} className="text-sm" />
                      </div>
                      <div className="col-span-2">
                        <HsnCombobox
                          value={item.hsn_sac}
                          onChange={(code) => updateItem(idx, "hsn_sac", code)}
                          onSelect={(entry) => {
                            if (entry) {
                              const updated = [...items];
                              updated[idx] = { ...updated[idx], hsn_sac: entry.code, gst_rate: String(entry.gst_rate) };
                              setItems(updated);
                            }
                          }}
                        />
                      </div>
                      <div className="col-span-1">
                        <Input type="number" placeholder="Qty" value={item.quantity} onChange={(e) => updateItem(idx, "quantity", e.target.value)} className="text-sm" />
                      </div>
                      <div className={showGstCol ? "col-span-2" : "col-span-3"}>
                        <Input type="number" placeholder="Rate" value={item.rate} onChange={(e) => updateItem(idx, "rate", e.target.value)} className="text-sm" />
                      </div>
                      {showGstCol && (
                        <div className="col-span-2">
                          <Select value={String(item.gst_rate)} onValueChange={(v) => updateItem(idx, "gst_rate", v)}>
                            <SelectTrigger className="text-sm"><SelectValue /></SelectTrigger>
                            <SelectContent>{GST_RATES.map((r) => <SelectItem key={r} value={String(r)}>{r}%</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                      )}
                      <div className="col-span-1 flex gap-1">
                        {items.length > 1 && (
                          <Button size="sm" variant="ghost" onClick={() => removeItem(idx)} className="text-red-500"><Trash2 className="h-3 w-3" /></Button>
                        )}
                      </div>
                    </div>
                    {/* Discount row */}
                    <div className="flex items-center gap-2 mt-2 ml-1">
                      <span className="text-[10px] text-[#94a3b8] font-medium uppercase tracking-wide">Discount:</span>
                      <Select value={item.discount_type || "none"} onValueChange={(v) => updateItem(idx, "discount_type", v === "none" ? "" : v)}>
                        <SelectTrigger className="text-xs h-7 w-24 border-dashed"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          <SelectItem value="percent">%</SelectItem>
                          <SelectItem value="fixed">Fixed (INR)</SelectItem>
                        </SelectContent>
                      </Select>
                      {item.discount_type && (
                        <Input type="number" placeholder={item.discount_type === "percent" ? "%" : "Amount"} value={item.discount_value} onChange={(e) => updateItem(idx, "discount_value", e.target.value)} className="text-xs h-7 w-20" />
                      )}
                    </div>
                  </div>
                ))}
                <Button size="sm" variant="outline" onClick={addItem} className="mt-1"><Plus className="h-3 w-3 mr-1" /> Add Item</Button>
              </div>

              <div>
                <Label className="text-sm font-medium">Notes (optional)</Label>
                <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Payment terms, notes..." className="mt-1.5" />
              </div>
              <Button onClick={createInvoice} disabled={creating} data-testid="submit-invoice-btn" className="w-full bg-[#4338ca] hover:bg-[#3730a3] rounded-xl">
                {creating ? "Creating..." : "Create Invoice"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94a3b8]" />
          <Input
            value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by invoice # or client..."
            data-testid="invoices-search-input"
            className="pl-9 h-10 border-[#e2e8f0]"
          />
        </div>
        <Tabs value={filter} onValueChange={setFilter}>
          <TabsList data-testid="invoice-filter-tabs">
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="draft">Draft</TabsTrigger>
            <TabsTrigger value="sent">Sent</TabsTrigger>
            <TabsTrigger value="paid">Paid</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Bulk Action Bar */}
      {selected.size > 0 && (
        <div className="flex items-center gap-3 px-4 py-3 bg-[#4338ca]/5 border border-[#4338ca]/20 rounded-xl" data-testid="bulk-action-bar">
          <CheckSquare className="h-4 w-4 text-[#4338ca]" />
          <span className="text-sm font-medium text-[#4338ca]">{selected.size} selected</span>
          <div className="flex gap-2 ml-auto">
            <Button size="sm" variant="outline" onClick={bulkExportCSV} data-testid="bulk-export-csv-invoices" className="text-xs rounded-lg">
              <Download className="h-3 w-3 mr-1" /> CSV
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="outline" data-testid="bulk-status-invoices" className="text-xs rounded-lg">
                  <MoreVertical className="h-3 w-3 mr-1" /> Status
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => executeBulk("sent")} data-testid="bulk-mark-sent">Mark Sent</DropdownMenuItem>
                <DropdownMenuItem onClick={() => executeBulk("paid")} data-testid="bulk-mark-paid">Mark Paid</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(true)} data-testid="bulk-delete-invoices" className="text-xs text-red-500 hover:text-red-600 rounded-lg">
              <Trash2 className="h-3 w-3 mr-1" /> Delete
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())} className="text-xs text-[#64748b]">Clear</Button>
          </div>
        </div>
      )}

      <Card className="bg-white border-[#e2e8f0] shadow-sm">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-sm text-[#64748b]">Loading...</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="h-12 w-12 text-[#e2e8f0] mx-auto mb-3" />
              <p className="text-sm text-[#64748b]">{search ? "No invoices match your search." : "No invoices found. Create one above or use the Command Center."}</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#e2e8f0] bg-[#f8fafc]">
                  <th className="py-3 px-4 w-10">
                    <Checkbox checked={selected.size === filtered.length && filtered.length > 0} onCheckedChange={toggleAll} data-testid="select-all-invoices" />
                  </th>
                  <th className="text-left py-3 px-4 text-[#64748b] font-medium">Invoice #</th>
                  <th className="text-left py-3 px-4 text-[#64748b] font-medium">Client</th>
                  <th className="text-left py-3 px-4 text-[#64748b] font-medium">Date</th>
                  <th className="text-right py-3 px-4 text-[#64748b] font-medium">Amount</th>
                  <th className="text-center py-3 px-4 text-[#64748b] font-medium">Status</th>
                  <th className="text-right py-3 px-4 text-[#64748b] font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((inv) => (
                  <tr key={inv.invoice_id} className="border-b border-[#f1f5f9] last:border-0 hover:bg-[#f8fafc] transition-colors duration-100" data-testid={`invoice-row-${inv.invoice_id}`}>
                    <td className="py-3 px-4">
                      <Checkbox checked={selected.has(inv.invoice_id)} onCheckedChange={() => toggleSelect(inv.invoice_id)} />
                    </td>
                    <td className="py-3 px-4 font-mono text-xs text-[#4338ca] font-medium">{inv.invoice_number}</td>
                    <td className="py-3 px-4 text-[#0f172a]">{inv.client_name}</td>
                    <td className="py-3 px-4 text-[#64748b]">{inv.date}</td>
                    <td className="py-3 px-4 text-right font-mono font-semibold text-[#0f172a]">{formatINR(inv.grand_total || inv.total)}</td>
                    <td className="py-3 px-4 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Badge className={inv.status === "paid" ? "bg-[#10b981] text-white" : inv.status === "sent" ? "bg-[#3b82f6] text-white" : "bg-[#f1f5f9] text-[#64748b]"}>
                          {inv.status}
                        </Badge>
                        {inv.is_export && (
                          <Badge className="bg-[#4338ca]/10 text-[#4338ca] text-[9px] px-1 py-0">
                            {inv.export_type === "lut" ? "LUT" : "IGST"}
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="sm" variant="ghost" data-testid={`invoice-actions-${inv.invoice_id}`}>
                            <MoreVertical className="h-3.5 w-3.5" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/invoices/${inv.invoice_id}/review`)} data-testid={`view-invoice-${inv.invoice_id}`}>
                            <Eye className="h-3.5 w-3.5 mr-2" /> View
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => deleteInvoice(inv.invoice_id)} className="text-red-500" data-testid={`delete-invoice-${inv.invoice_id}`}>
                            <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Bulk Delete Confirm */}
      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-outfit text-xl flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" /> Confirm Delete
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {selected.size} invoice(s)? This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setConfirmDelete(false)}>Cancel</Button>
            <Button onClick={bulkDelete} data-testid="confirm-bulk-delete-invoices" className="bg-red-500 hover:bg-red-600 text-white">
              Delete {selected.size} Invoices
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
